import { useEffect, useRef, useState } from "react";
import { buildLut, lutToTexture, LUT_SIZE } from "@/lib/grade";
import { createRenderer } from "@/lib/glLut";
import { PathDots } from "@/components/editor/PathDots";
import { FxTransformBox } from "@/components/editor/FxTransformBox";

const SAMPLE = "https://images.unsplash.com/photo-1616749328467-9af5031949a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&h=900&fit=crop";
const ready = (el) => el && el.readyState >= 2;

export const Viewer = ({ hasClips, grade, compare, videoRef, videoBRef, fxRef, viewRef, canvasRef, selectedFx, path = [], keyPoints = [], onKfSeek, onKfMove, onFxChange }) => {
  const renderer = useRef(null);
  const img = useRef(null);
  const state = useRef({ compare, hasClips });
  state.current = { compare, hasClips };
  const [decodeError, setDecodeError] = useState(false);
  const outer = useRef(null);
  const [fit, setFit] = useState({ cw: 16, ch: 9, W: 0, H: 0 });
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setFit((f) => ({ ...f, W: e.contentRect.width, H: e.contentRect.height })));
    ro.observe(outer.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    renderer.current = createRenderer(canvasRef.current);
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => (img.current = im);
    im.src = SAMPLE;
    const black = document.createElement("canvas");
    let raf;
    const loop = () => {
      const r = renderer.current, vs = viewRef.current || {};
      if (r) {
        const layers = (vs.layers || []).filter((l) => ready(l.el));
        let base = ready(vs.a) ? vs.a : !state.current.hasClips ? img.current : null;
        if (!base && layers.length) {
          const fx = layers[0].el;
          if (black.width !== fx.videoWidth) { black.width = fx.videoWidth; black.height = fx.videoHeight; }
          base = black;
        }
        if (base) r.draw({ ...vs, a: base, b: ready(vs.b) ? vs.b : null }, state.current.compare ? 0.5 : 0, layers);
        else if (!vs.a) r.clear();
      }
      const cv = canvasRef.current;
      setFit((f) => (f.cw === cv.width && f.ch === cv.height ? f : { ...f, cw: cv.width, ch: cv.height }));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef, viewRef]);

  useEffect(() => {
    renderer.current?.setLut(lutToTexture(buildLut(grade, LUT_SIZE), LUT_SIZE), LUT_SIZE);
  }, [grade]);

  const scale = fit.W && fit.H ? Math.min(fit.W / fit.cw, fit.H / fit.ch) : 0;
  return (
    <div ref={outer} className="relative w-full h-full flex items-center justify-center bg-black/60 rounded-md" data-testid="viewer">
      <div className="relative" style={{ width: fit.cw * scale, height: fit.ch * scale }} data-testid="viewer-frame">
        <canvas ref={canvasRef} className="block w-full h-full" data-testid="viewer-canvas" />
        {compare && (
          <>
            <div className="absolute inset-y-0 left-1/2 w-px bg-[#FFB800] shadow-[0_0_10px_#FFB800]" />
            <span className="absolute top-2 left-2 font-mono-ui text-[10px] bg-black/70 px-1.5 py-0.5 rounded">BEFORE</span>
            <span className="absolute top-2 right-2 font-mono-ui text-[10px] bg-black/70 px-1.5 py-0.5 rounded text-[#FFB800]">AFTER</span>
          </>
        )}
        {!hasClips && <span className="absolute bottom-2 left-2 font-mono-ui text-[10px] bg-black/70 px-2 py-1 rounded text-slate-300" data-testid="viewer-sample-label">SAMPLE FRAME · add footage to the timeline</span>}
        {hasClips && decodeError && <span className="absolute inset-x-4 top-1/2 -translate-y-1/2 text-center text-xs bg-black/80 border border-red-500/40 text-red-300 px-3 py-2 rounded" data-testid="viewer-decode-error">This browser can't preview this clip's codec. It will still export — or convert to H.264/MP4 or WebM for preview.</span>}
        {path.length > 1 && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible z-30" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="fx-motion-path">
            <polyline points={path.map((q) => `${50 + q.x * 50},${50 - q.y * 50}`).join(" ")} fill="none" stroke="#EC4899" strokeWidth="1.5" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
          </svg>
        )}
        <PathDots points={keyPoints} onSeek={onKfSeek} onMove={onKfMove} />
        {selectedFx && <FxTransformBox fx={selectedFx} onChange={onFxChange} />}
      </div>
      <video ref={videoRef} playsInline preload="auto" className="absolute w-px h-px opacity-0 pointer-events-none" data-testid="viewer-video"
        onError={() => setDecodeError(true)} onLoadedData={() => setDecodeError(false)} />
      <video ref={videoBRef} playsInline preload="auto" className="absolute w-px h-px opacity-0 pointer-events-none" data-testid="viewer-video-b"
        onError={() => setDecodeError(true)} onLoadedData={() => setDecodeError(false)} />
      {[0, 1, 2, 3].map((i) => (
        <video key={i} ref={(el) => (fxRef.current[i] = el)} playsInline muted preload="auto" className="absolute w-px h-px opacity-0 pointer-events-none" data-testid={`viewer-fx-video-${i}`} />
      ))}
    </div>
  );
};
